import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import StylePack from './style-pack';
import { GreenroomState as GS } from '../../stores/types/types';
import StylePacksFilters from './style-pack-filters';
import StylePacksMenu from './style-packs-menu';
import ButtonClose from '../general-components/button-close';
import { ContentTemplate, STYLE_PACK_FILTER, StylePackContentType, StylePackData, StylePackGroup } from '../types/stylepacks';

export const templateOrder = [
	StylePackContentType.TITLE,
	StylePackContentType.WALKUP,
	StylePackContentType.SEGMENT,
	StylePackContentType.COUNTDOWN,
	StylePackContentType.BOOKEND,
	StylePackContentType.SLIDE,
	// StylePackContentType.BACKGROUND,
	// StylePackContentType.TRANSITION,
];

export const contentFilterOptions = [
	{ label: 'All Content', value: undefined },
	{ label: 'Walkups', value: StylePackContentType.WALKUP },
	{ label: 'Segments', value: StylePackContentType.SEGMENT },
	{ label: 'Lowers', value: StylePackContentType.TITLE },
	{ label: 'Countdowns', value: StylePackContentType.COUNTDOWN },
	{ label: 'Transitions', value: StylePackContentType.TRANSITION },
];

const filterCapsuleAndLocked = (t:ContentTemplate) => t.provider === 'capsule' && !t.locked;

export interface StylePacksTabProps {
	setStylePackInfo: (stylePackInfo: StylePackData, id: string) => void;
	closeStylePackTab: () => void;
}

export default function StylePacksTab(props: StylePacksTabProps) {
	const { setStylePackInfo, closeStylePackTab } = props;

	const loadingTemplates  = useSelector((s: GS) => s.GeneratedContentReducer.loadingTemplates);
	const contentTemplates = useSelector((s: GS) => s.GeneratedContentReducer.contentTemplates);
	const stylePackTypeFilter = useSelector((s: GS) => s.UserInterfaceReducer.stylePackTypeFilter);

	const channelName = useSelector((s: GS) => s.SessionReducer.meeting?.channel_name);

	const [filterOptions, setFilterOptions] = useState<string[]>(Object.values(STYLE_PACK_FILTER));
	// null is no search, [] is no results
	const [filteredStylePacks, setFilteredStylePacks] = useState<StylePackData[] | null>(null);

	useEffect(() => {
		if (channelName) {
			setFilterOptions([
				...Object.values(STYLE_PACK_FILTER),
				channelName,
			]);
		}
	}, [channelName]);

	const stylePacks = useMemo<StylePackData[]>(() => {
		if (loadingTemplates || contentTemplates.length === 0) {
			return [];
		}

		const stylepackTemplates = contentTemplates.filter(filterCapsuleAndLocked);

		const grouped = stylepackTemplates.reduce((acc, template) => {
			const stylePackId = template.stylePack;
			const contentType = template.contentType;

			if (!acc[stylePackId]) {
				acc[stylePackId] = {
					id: stylePackId,
					name: template.name,
					locked: false,
					stylePack: [],
					groupMap: new Map<string, StylePackGroup>(),
					strict: false
				};
			}

			const pack = acc[stylePackId];

			// Group by contentType
			if (contentType) {
				if (!pack.groupMap.has(contentType)) {
					const group: StylePackGroup = { type: contentType, templates: [] };
					pack.groupMap.set(contentType, group);
					pack.stylePack.push(group);
				}
				pack.groupMap.get(contentType)?.templates.push(template);

				if (!pack.strict && template.data?.strict)
					pack.strict = true;
			}

			return acc;
		}, {} as Record<string, StylePackData & { groupMap: Map<string, StylePackGroup> }>);

		// this is such a pain to figure out so - these are grouped like this so that the search worker
		// in src/workers/search-style-packs.ts does not have to search through every template and gather them
		// they are grouped so we are iterating/searching over 10 items not 100 from here on out
		return Object.values(grouped).map(({ id, name, locked, stylePack, strict }) => ({
			id,
			name,
			locked,
			stylePack, // all of the templates in the pack
			strict,
		}));
	}, [contentTemplates, loadingTemplates]);

	const renderedStylePacks = useMemo<StylePackData[]>(() => {
		// assume StylePacksMenu has created the viewable stylepacks
		if (filteredStylePacks) return filteredStylePacks
		// assume the StylePack component will filter items based on stylePackTypeFilter
		return stylePacks;
	}, [filteredStylePacks, stylePackTypeFilter, stylePacks]);

	return (
		<div className="style-packs-tab">
			<div className="magic-telly-wand-gradient" data-testid="magic-telly-wand-gradient" />
			<div className='style-pack-info-content-header tab-header'>
				<h3 className='bl-font-header'>Stylepacks</h3>
				<sup className='bl-font-header'>TM</sup>
				<ButtonClose onClick={closeStylePackTab} noPadding />
			</div>

			<StylePacksFilters />
			<StylePacksMenu filters={filterOptions} items={stylePacks} onResult={setFilteredStylePacks}/>

			{<div
				className={classNames('style-packs', { filtered: stylePackTypeFilter !== undefined })}
				data-testid="style-packs"
			>
				{renderedStylePacks.map((group) => <StylePack
					key={group.id}
					id={group.id}
					name={group.name}
					content={group.stylePack}
					setStylePackInfo={setStylePackInfo}
					locked={group.locked}
					filter={stylePackTypeFilter}
					strict={group.strict}
				/>)}
			</div>}
		</div>
	);
}
